import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Spinner,
  VStack,
  Badge,
  Heading,
  Flex,
  useColorModeValue,
} from "@chakra-ui/react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { firestore } from "../../firebase/firebaseConfig";
import { REWARD_TIERS } from "../../utils/rewards";

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue("white", "gray.700");
  const cardHoverBg = useColorModeValue("gray.50", "gray.600");
  const primaryText = useColorModeValue("gray.800", "whiteAlpha.900");
  const secondaryText = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const usersQuery = query(collection(firestore, "users"));
        const usersSnapshot = await getDocs(usersQuery);

        let usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().userName || "Anonymous",
          totalVerifiedClicks: 0,
        }));

        for (let user of usersData) {
          const sharesQuery = query(
            collection(firestore, "user_shares"),
            where("userId", "==", user.id)
          );
          const sharesSnapshot = await getDocs(sharesQuery);

          user.totalVerifiedClicks = sharesSnapshot.docs.reduce(
            (sum, doc) => sum + (doc.data().verifiedClicks || 0),
            0
          );
        }

        usersData = usersData
          .sort((a, b) => b.totalVerifiedClicks - a.totalVerifiedClicks)
          .slice(0, 10);

        setLeaderboard(usersData);
      } catch (error) {
        console.error("❌ Error fetching leaderboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  return (
    <Box py={12} px={6} maxW="800px" mx="auto">
      <Heading
        size="xl"
        textAlign="center"
        mb={10}
        color={primaryText}
      >
        🏆 Leaderboard
      </Heading>

      {loading ? (
        <Flex direction="column" align="center" justify="center">
          <Spinner size="lg" color="blue.500" />
          <Text mt={4} fontSize="md" color={secondaryText}>
            Loading leaderboard...
          </Text>
        </Flex>
      ) : (
        <VStack spacing={6} align="stretch">
          {leaderboard.length === 0 ? (
            <Text
              textAlign="center"
              color={secondaryText}
              fontSize="md"
            >
              No leaderboard data available.
            </Text>
          ) : (
            leaderboard.map((user, index) => {
              const highestMilestone = Object.keys(REWARD_TIERS)
                .reverse()
                .find((threshold) => user.totalVerifiedClicks >= threshold);

              return (
                <Box
                  key={user.id}
                  p={6}
                  bg={cardBg}
                  rounded="lg"
                  boxShadow="sm"
                  transition="all 0.3s"
                  _hover={{
                    boxShadow: "md",
                    bg: cardHoverBg,
                    transform: "translateY(-2px)",
                  }}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    mb={2}
                  >
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color={primaryText}
                    >
                      #{index + 1} {user.name}
                    </Text>

                    {highestMilestone && (
                      <Badge
                        colorScheme="green"
                        fontSize="0.8em"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        🎉 {REWARD_TIERS[highestMilestone]}
                      </Badge>
                    )}
                  </Flex>

                  <Text
                    fontSize="sm"
                    color={secondaryText}
                  >
                    🔗 {user.totalVerifiedClicks.toLocaleString()} verified clicks
                  </Text>
                </Box>
              );
            })
          )}
        </VStack>
      )}
    </Box>
  );
};

export default LeaderboardPage;
